import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Video } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    COUNTRIES,
    DAYS,
    DEFAULT_COUNTRY_CODE,
    LEVELS,
    LEVEL_ORDER,
    LevelKey,
    TimeField,
} from './liveSessionsConfig';

interface LiveSession {
    id: number;
    title: string;
    level: LevelKey;
    teacher: string;
    day: string;
    day_order: number;
    time_es: string;
    time_ar: string;
    time_co: string;
    time_mx: string;
    time_ec: string;
    meet_url: string | null;
    active: boolean;
    status: string;
}

interface Props {
    live_sessions: LiveSession[];
}

const ALL_LEVELS = 'all';

// Times arrive raw (e.g. "01:00*"); the trailing "*" means the class falls on the
// next day in that timezone.
const parseTime = (raw: string): { display: string; nextDay: boolean } => {
    const nextDay = raw.endsWith('*');
    return { display: nextDay ? raw.slice(0, -1) : raw, nextDay };
};

const LiveSessionsContent: React.FC<Props> = ({ live_sessions }) => {
    const { t } = useTranslation();
    const [levelFilter, setLevelFilter] = useState<LevelKey | typeof ALL_LEVELS>(ALL_LEVELS);
    const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY_CODE);

    const country = COUNTRIES.find((c) => c.code === countryCode) ?? COUNTRIES[0];
    const timeField: TimeField = country.timeField;

    const sessionsByDay = useMemo(() => {
        const filtered = live_sessions.filter(
            (session) => levelFilter === ALL_LEVELS || session.level === levelFilter
        );
        return DAYS.map((day) => ({
            day,
            sessions: filtered
                .filter((session) => session.day_order === day.order)
                .sort((a, b) => (a[timeField] || '').localeCompare(b[timeField] || '')),
        }));
    }, [live_sessions, levelFilter, timeField]);

    const hasSessions = live_sessions.length > 0;

    return (
        <AppLayout>
            <PageTitle title="Live Sessions" />
            <div className="mx-auto max-w-7xl space-y-8">
                {/* Header */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
                    <h1 className="mb-4 text-center text-3xl font-bold text-gray-900 dark:text-white">
                        🎌 {t('liveSessions.title')}
                    </h1>
                    <ul className="space-y-2 text-gray-700 dark:text-neutral-300">
                        <li>{t('liveSessions.intro.line1')}</li>
                        <li>✅ {t('liveSessions.intro.line2')}</li>
                        <li>✅ {t('liveSessions.intro.line3')}</li>
                        <li>❗ {t('liveSessions.intro.line4')}</li>
                    </ul>
                    <div className="mt-4 rounded-lg border-l-4 border-theme-primary bg-theme-lightest p-3 text-sm text-gray-700 dark:text-neutral-300">
                        💡 {t('liveSessions.intro.recommendation')}
                    </div>
                </div>

                {/* Levels legend */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
                    <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                        🎓 {t('liveSessions.legendTitle')}
                    </h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {LEVEL_ORDER.map((key) => {
                            const level = LEVELS[key];
                            const cefr = level.cefr.includes('.') ? t(level.cefr) : level.cefr;
                            return (
                                <div key={key} className="flex flex-col">
                                    <div
                                        className="rounded-t-lg px-4 py-3 text-white"
                                        style={{ backgroundColor: level.color }}
                                    >
                                        <div className="font-bold">{t(level.labelKey)}</div>
                                        <div className="text-xs opacity-90">{cefr}</div>
                                    </div>
                                    <p className="rounded-b-lg border border-t-0 border-gray-200 p-3 text-sm text-gray-600 dark:border-neutral-700 dark:text-neutral-400">
                                        {t(level.descKey)}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-gray-700 dark:text-neutral-300">
                            🎓 {t('liveSessions.filterLevel')}
                        </span>
                        <select
                            value={levelFilter}
                            onChange={(e) => setLevelFilter(e.target.value as LevelKey | typeof ALL_LEVELS)}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                        >
                            <option value={ALL_LEVELS}>{t('liveSessions.allLevels')}</option>
                            {LEVEL_ORDER.map((key) => (
                                <option key={key} value={key}>
                                    {t(LEVELS[key].labelKey)}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-gray-700 dark:text-neutral-300">
                            📍 {t('liveSessions.filterCountry')}
                        </span>
                        <select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c.code} value={c.code}>
                                    {c.flag} {c.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                {/* Weekly grid */}
                {hasSessions ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        {sessionsByDay.map(({ day, sessions }) => (
                            <div key={day.key} className="space-y-3">
                                <h3 className="text-center text-lg font-bold text-gray-900 dark:text-white">
                                    {t(day.labelKey)}
                                </h3>
                                {sessions.map((session) => {
                                    const level = LEVELS[session.level] ?? LEVELS.general;
                                    const { display, nextDay } = parseTime(session[timeField] || '');
                                    return (
                                        <div
                                            key={session.id}
                                            className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
                                        >
                                            <div
                                                className="px-3 py-2 font-bold text-white"
                                                style={{ backgroundColor: level.color }}
                                            >
                                                {session.title}
                                            </div>
                                            <div className="space-y-2 p-3">
                                                {session.teacher ? (
                                                    <div className="text-sm text-gray-700 dark:text-neutral-300">
                                                        {session.teacher}
                                                    </div>
                                                ) : null}
                                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {country.flag} {display}
                                                    {nextDay ? (
                                                        <span className="ml-1 text-xs font-normal text-gray-500 dark:text-neutral-400">
                                                            ({t('liveSessions.nextDayNote')})
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {session.meet_url ? (
                                                    <a
                                                        href={session.meet_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="block w-full rounded-lg bg-theme-primary px-3 py-2 text-center text-sm font-semibold text-theme-on-primary transition-colors hover:bg-theme-primary-hover"
                                                    >
                                                        {t('liveSessions.joinButton')}
                                                    </a>
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
                        <div className="py-12 text-center">
                            <Video className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-neutral-500" />
                            <p className="text-lg text-gray-600 dark:text-neutral-400">
                                {t('liveSessions.empty')}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </AppLayout>
    );
};

const LiveSessions: React.FC<Props> = ({ live_sessions }) => (
    <ThemeWrapper>
        <LiveSessionsContent live_sessions={live_sessions} />
    </ThemeWrapper>
);

export default LiveSessions;
